import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RowsChangeData } from 'react-data-grid';
import { Employee } from '../types';
import {
  API_URL,
  DEFAULT_ROW_HEIGHT,
  NOTES_EXTRA_SPACE
} from '../constants/appConstants';

export type ResolveEmployeeAction = 'existing' | 'linked' | 'created' | 'updated';

export interface ResolveEmployeeResult {
  employee: Employee;
  action: ResolveEmployeeAction;
}

export const useEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [rowHeights, setRowHeights] = useState<Record<number, number>>({});

  const autoSaveEmployee = useCallback(async (employee: Employee) => {
    try {
      await axios.put(`${API_URL}/employees/${employee.id}`, employee);
      setError(null);
    } catch (err) {
      console.error('Auto-save error:', err);
      setError('Failed to auto-save changes. Please check your connection.');
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<Employee[]>(`${API_URL}/employees`);
      setEmployees(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees. Make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    setRowHeights(prev => {
      const next = { ...prev };
      let changed = false;
      employees.forEach(emp => {
        if (next[emp.id] == null) {
          next[emp.id] = DEFAULT_ROW_HEIGHT;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [employees]);

  const resolveEmployee = useCallback(
    async (employee: Employee): Promise<ResolveEmployeeResult> => {
      if (employee.id > 0) {
        return { employee, action: 'existing' };
      }

      const byIntranetId = employee.intranet_id
        ? employees.find((row) => String(row.intranet_id) === String(employee.intranet_id))
        : undefined;
      if (byIntranetId) {
        return { employee: byIntranetId, action: 'linked' };
      }

      const normalizedName = employee.name.toLowerCase().trim();
      const byName = employees.find(
        (row) => row.name.toLowerCase().trim() === normalizedName
      );
      if (byName) {
        const needsUpdate =
          (byName.department || '').toLowerCase() !== 'phishingbox' ||
          (employee.intranet_id && byName.intranet_id !== employee.intranet_id);

        if (!needsUpdate) {
          return { employee: byName, action: 'linked' };
        }

        const updatedEmployee: Employee = {
          ...byName,
          department: 'PhishingBox',
          intranet_id: employee.intranet_id ?? byName.intranet_id ?? null,
        };
        const response = await axios.put<Employee>(
          `${API_URL}/employees/${byName.id}`,
          updatedEmployee
        );
        setEmployees((prev) =>
          prev.map((row) => (row.id === byName.id ? response.data : row))
        );
        setError(null);
        return { employee: response.data, action: 'updated' };
      }

      const payload: Partial<Employee> = {
        name: employee.name,
        department: employee.department || 'PhishingBox',
        position: employee.position || 'Developer',
        points: employee.points ?? 10,
        performance_score: employee.performance_score,
        notes: employee.notes,
        intranet_id: employee.intranet_id ?? null,
      };

      const response = await axios.post<Employee>(`${API_URL}/employees`, payload);
      setEmployees((prev) => [...prev, response.data]);
      setError(null);
      return { employee: response.data, action: 'created' };
    },
    [employees]
  );

  const addEmployee = useCallback(async (team: 'elink' | 'phishingbox' = 'elink'): Promise<Employee | null> => {
    const newEmployee: Partial<Employee> = {
      name: '',
      department: team === 'phishingbox' ? 'PhishingBox' : 'Development',
      position: '',
      points: 10,
      performance_score: null,
      notes: null
    };

    try {
      const response = await axios.post<Employee>(`${API_URL}/employees`, newEmployee);
      setEmployees(prev => [...prev, response.data]);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error adding employee:', err);
      setError('Failed to add new employee');
      return null;
    }
  }, []);

  const deleteEmployee = useCallback(async (id: number): Promise<Employee | null> => {
    const target = employees.find((employee) => employee.id === id) ?? null;
    try {
      await axios.delete(`${API_URL}/employees/${id}`);
      setEmployees(prev => prev.filter(emp => emp.id !== id));
      setError(null);
      return target;
    } catch (err) {
      console.error('Error deleting employee:', err);
      setError('Failed to delete employee');
      return null;
    }
  }, [employees]);

  const saveAllEmployees = useCallback(async () => {
    setSaving(true);
    try {
      await Promise.all(employees.map(employee => autoSaveEmployee(employee)));
      setError(null);
      return true;
    } catch (err) {
      console.error('Error saving employees:', err);
      setError('Failed to save changes');
      return false;
    } finally {
      setSaving(false);
    }
  }, [employees, autoSaveEmployee]);

  const handleNotesChange = useCallback((id: number, value: string) => {
    setEmployees(prev =>
      prev.map(emp => (emp.id === id ? { ...emp, notes: value } : emp))
    );
  }, []);

  const handleNotesBlur = useCallback(
    async (employee: Employee) => {
      let target = employee;
      if (employee.id <= 0) {
        try {
          const result = await resolveEmployee(employee);
          target = result.employee;
        } catch (err) {
          console.error('Error provisioning employee for notes:', err);
          setError('Failed to add employee to the performance roster');
          return;
        }
      }

      const trimmed = (target.notes || '').trim();
      const finalValue = trimmed === '' ? null : trimmed;
      const updatedEmployee = { ...target, notes: finalValue };
      setEmployees(prev =>
        prev.map(emp => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
      );
      autoSaveEmployee(updatedEmployee);
    },
    [autoSaveEmployee, resolveEmployee]
  );

  const syncRowHeight = useCallback((id: number, textareaHeight: number) => {
    const newHeight = Math.max(DEFAULT_ROW_HEIGHT, textareaHeight + NOTES_EXTRA_SPACE);
    setRowHeights(prev => {
      if (Math.abs((prev[id] ?? DEFAULT_ROW_HEIGHT) - newHeight) < 1) {
        return prev;
      }
      return { ...prev, [id]: newHeight };
    });
  }, []);

  const rowHeightGetter = useCallback(
    (row: Employee) => rowHeights[row.id] ?? DEFAULT_ROW_HEIGHT,
    [rowHeights]
  );

  const handleRowsChange = useCallback(
    (rows: Employee[], data: RowsChangeData<Employee>) => {
      const updatedRows = rows.map(row => ({ ...row }));
      setEmployees(updatedRows);

      let changedIndexes = data?.indexes ?? [];
      if (changedIndexes.length === 0 && employees.length === updatedRows.length) {
        changedIndexes = updatedRows.reduce<number[]>((acc, row, index) => {
          const prevRow = employees[index];
          if (JSON.stringify(prevRow) !== JSON.stringify(row)) {
            acc.push(index);
          }
          return acc;
        }, []);
      }

      changedIndexes.forEach(index => {
        const updatedEmployee = updatedRows[index];
        if (updatedEmployee && updatedEmployee.id > 0) {
          autoSaveEmployee(updatedEmployee);
        }
      });
    },
    [employees, autoSaveEmployee]
  );

  return {
    employees,
    loading,
    error,
    saving,
    rowHeights,
    rowHeightGetter,
    handleRowsChange,
    handleNotesChange,
    handleNotesBlur,
    syncRowHeight,
    fetchEmployees,
    addEmployee,
    deleteEmployee,
    saveAllEmployees,
    resolveEmployee,
    setError
  };
};

